// ─── Lead Scoring — fonte única de verdade ──────────────────────────────────
//
// Antes deste arquivo, o catálogo de sinais vivia em server/leadScoring.ts e os
// limiares de temperatura estavam duplicados em cinco lugares (serviço, router,
// página, badge e os defaults de getHot/Warm/ColdLeads) — com um off-by-one
// entre eles: score 50 era "warm" nas estatísticas e "hot" na lista de hot
// leads. Aqui é o único lugar que define ambos.
//
// Importado por server/ e client/ — não pode depender de Node nem de Drizzle.

/** Canal que originou o sinal. Usado para agrupar o breakdown na UI. */
export type SignalChannel = "email" | "whatsapp" | "crm" | "site" | "system";

export type SignalDefinition = {
  /** Chave persistida em score_events.eventType. */
  type: string;
  label: string;
  channel: SignalChannel;
  /** Pontuação padrão. Uma linha em scoring_rules sobrescreve. */
  points: number;
  /**
   * Se o peso do sinal decai com o tempo. Sinais negativos de consentimento
   * (descadastro, spam) NÃO decaem: são fatos permanentes sobre a permissão do
   * contato, não medida de engajamento recente.
   */
  decays: boolean;
  /**
   * Teto diário de ocorrências que pontuam (0 = sem teto). Impede que volume
   * bruto vire calor falso — 40 aberturas do mesmo e-mail não valem 40x.
   */
  maxPerDay: number;
  /**
   * Se existe código emitindo este sinal hoje. Os `false` são herdados do
   * catálogo original, que listava 13 regras das quais 11 nunca tiveram
   * emissor — o usuário editava os pontos e nada acontecia. Mantidos para não
   * orfanar as linhas já gravadas em scoring_rules, mas marcados para a UI
   * poder separá-los do que realmente coleta.
   */
  emitted: boolean;
  description: string;
};

export const SIGNAL_CATALOG: readonly SignalDefinition[] = [
  // ─── E-mail (emitidos pelo webhook do SendGrid) ───────────────────────────
  {
    type: "email_open", label: "Abriu o e-mail", channel: "email",
    points: 5, decays: true, maxPerDay: 3, emitted: true,
    description: "Contato abriu uma mensagem enviada por você.",
  },
  {
    type: "email_click", label: "Clicou em link do e-mail", channel: "email",
    points: 10, decays: true, maxPerDay: 5, emitted: true,
    description: "Sinal de intenção mais forte que a abertura.",
  },
  {
    type: "email_delivered", label: "E-mail entregue", channel: "email",
    points: 1, decays: true, maxPerDay: 2, emitted: true,
    description: "Endereço válido e caixa ativa.",
  },
  {
    type: "email_bounce", label: "E-mail retornou", channel: "email",
    points: -10, decays: false, maxPerDay: 0, emitted: true,
    description: "Endereço inválido ou caixa indisponível.",
  },
  {
    type: "email_dropped", label: "E-mail descartado", channel: "email",
    points: -5, decays: false, maxPerDay: 0, emitted: true,
    description: "Bloqueado antes do envio (endereço em lista de supressão).",
  },
  {
    type: "email_unsubscribe", label: "Descadastrou-se", channel: "email",
    points: -50, decays: false, maxPerDay: 0, emitted: true,
    description: "Pediu para não receber mais. Nunca é oportunidade.",
  },
  {
    type: "email_spam_report", label: "Marcou como spam", channel: "email",
    points: -80, decays: false, maxPerDay: 0, emitted: true,
    description: "Sinal negativo mais grave. Nunca é oportunidade.",
  },

  // ─── Fluxos (ação update_score do Flow Builder) ───────────────────────────
  {
    type: "flow_action", label: "Pontuação por fluxo", channel: "system",
    points: 0, decays: true, maxPerDay: 0, emitted: true,
    description: "Pontos atribuídos por um nó de fluxo. O peso vem do próprio nó.",
  },

  // ─── Herdados sem emissor — ver comentário de `emitted` ───────────────────
  { type: "email_conversion", label: "Converteu pelo e-mail", channel: "email", points: 50, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "message_received", label: "Enviou mensagem", channel: "whatsapp", points: 3, decays: true, maxPerDay: 3, emitted: false, description: "Reservado para a ingestão de WhatsApp." },
  { type: "tag_added", label: "Recebeu etiqueta", channel: "system", points: 5, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "contact_created", label: "Contato criado", channel: "system", points: 0, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "form_submission", label: "Enviou formulário", channel: "site", points: 20, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "content_download", label: "Baixou material", channel: "site", points: 20, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "demo_request", label: "Pediu demonstração", channel: "site", points: 50, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "call_scheduled", label: "Agendou conversa", channel: "crm", points: 30, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "website_visit", label: "Visitou o site", channel: "site", points: 3, decays: true, maxPerDay: 5, emitted: false, description: "Reservado." },
  { type: "website_time_2min", label: "Ficou 2min no site", channel: "site", points: 5, decays: true, maxPerDay: 0, emitted: false, description: "Reservado." },
  { type: "page_view", label: "Viu uma página", channel: "site", points: 1, decays: true, maxPerDay: 10, emitted: false, description: "Reservado." },
] as const;

const CATALOG_BY_TYPE = new Map(SIGNAL_CATALOG.map((s) => [s.type, s]));

export function getSignalDefinition(type: string): SignalDefinition | null {
  return CATALOG_BY_TYPE.get(type) ?? null;
}

/**
 * Mapa `eventType → pontos`, formato histórico consumido por
 * server/leadScoring.ts e pelos testes existentes.
 */
export const DEFAULT_SCORING_RULES: Record<string, number> = Object.fromEntries(
  SIGNAL_CATALOG.map((s) => [s.type, s.points]),
);

// ─── Temperatura ────────────────────────────────────────────────────────────

export type LeadTemperature = "cold" | "warm" | "hot";

/**
 * Limiares inclusivos no piso: cold ≤ 24, warm 25–49, hot ≥ 50.
 *
 * A faixa de "warm" fechava em 50 inclusive enquanto getHotLeads() começava em
 * 50 — o mesmo lead aparecia nas duas listas. Fronteira única aqui.
 */
export const TEMPERATURE_THRESHOLDS = { warm: 25, hot: 50 } as const;

export function resolveTemperature(score: number): LeadTemperature {
  if (score >= TEMPERATURE_THRESHOLDS.hot) return "hot";
  if (score >= TEMPERATURE_THRESHOLDS.warm) return "warm";
  return "cold";
}

export const TEMPERATURE_LABELS: Record<LeadTemperature, string> = {
  cold: "Frio",
  warm: "Morno",
  hot: "Quente",
};

/**
 * Limites abertos das faixas. `totalScore` é `integer` no Postgres, então os
 * extremos precisam caber em int4 — Number.MAX_SAFE_INTEGER estoura a coluna e
 * a comparação falha em runtime.
 */
export const SCORE_BOUND = 2_000_000_000;

/** Faixa numérica de cada temperatura, para queries por intervalo. */
export function temperatureRange(t: LeadTemperature): { min: number; max: number } {
  if (t === "hot") return { min: TEMPERATURE_THRESHOLDS.hot, max: SCORE_BOUND };
  if (t === "warm") return { min: TEMPERATURE_THRESHOLDS.warm, max: TEMPERATURE_THRESHOLDS.hot - 1 };
  return { min: -SCORE_BOUND, max: TEMPERATURE_THRESHOLDS.warm - 1 };
}

// ─── Decay ──────────────────────────────────────────────────────────────────

/**
 * Meia-vida do engajamento, em dias. Aos 30 dias um sinal vale metade; aos 60,
 * um quarto. Sem isso o score só sobe e, em alguns meses, a base inteira fica
 * "quente" — o ranking perde qualquer utilidade.
 */
export const DEFAULT_HALF_LIFE_DAYS = 30;

/**
 * Peso efetivo de um sinal, dado quanto tempo passou.
 *
 * Espelha exatamente a expressão SQL do recálculo em server/leadScoring.ts — as
 * duas precisam concordar, e é isto que os testes fixam. Eventos com data
 * futura (relógio dessincronizado num webhook) são tratados como "agora": não
 * decaem, mas também não são amplificados.
 */
export function decayFactor(
  occurredAt: Date,
  now: Date = new Date(),
  halfLifeDays: number = DEFAULT_HALF_LIFE_DAYS,
): number {
  if (halfLifeDays <= 0) return 1;
  const elapsedMs = Math.max(0, now.getTime() - occurredAt.getTime());
  const elapsedDays = elapsedMs / 86_400_000;
  return Math.pow(0.5, elapsedDays / halfLifeDays);
}
